/**
 * Permission constants for role-based access control
 * Defines all available permissions in the system
 */

export const PERMISSIONS = {
  // Invoice Template Design
  INVOICE_DESIGN_VIEW: 'invoice_design.view',
  INVOICE_DESIGN_CREATE: 'invoice_design.create',
  INVOICE_DESIGN_EDIT: 'invoice_design.edit',
  INVOICE_DESIGN_DELETE: 'invoice_design.delete',
  
  // Company Details
  COMPANY_VIEW: 'company.view',
  COMPANY_EDIT: 'company.edit',
  
  // Shipping Rates
  SHIPPING_VIEW: 'shipping.view',
  SHIPPING_CREATE: 'shipping.create',
  SHIPPING_EDIT: 'shipping.edit',
  SHIPPING_DELETE: 'shipping.delete',
  
  // Customers
  CUSTOMERS_VIEW: 'customers.view',
  CUSTOMERS_CREATE: 'customers.create',
  CUSTOMERS_EDIT: 'customers.edit',
  CUSTOMERS_DELETE: 'customers.delete',
  
  // Invoices
  INVOICES_VIEW: 'invoices.view',
  INVOICES_CREATE: 'invoices.create',
  INVOICES_EDIT: 'invoices.edit',
  INVOICES_DELETE: 'invoices.delete',
  INVOICES_CANCEL: 'invoices.cancel',
  
  // Settings
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',
  
  // Number Schemas
  NUMBER_SCHEMA_VIEW: 'number_schema.view',
  NUMBER_SCHEMA_EDIT: 'number_schema.edit',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Role-based permission sets
 * Defines which permissions each role has by default
 */
export const ROLE_PERMISSIONS: Record<string, Record<string, boolean>> = {
  Admin: {
    // Full access to everything
    [PERMISSIONS.INVOICE_DESIGN_VIEW]: true,
    [PERMISSIONS.INVOICE_DESIGN_CREATE]: true,
    [PERMISSIONS.INVOICE_DESIGN_EDIT]: true,
    [PERMISSIONS.INVOICE_DESIGN_DELETE]: true,
    [PERMISSIONS.COMPANY_VIEW]: true,
    [PERMISSIONS.COMPANY_EDIT]: true,
    [PERMISSIONS.SHIPPING_VIEW]: true,
    [PERMISSIONS.SHIPPING_CREATE]: true,
    [PERMISSIONS.SHIPPING_EDIT]: true,
    [PERMISSIONS.SHIPPING_DELETE]: true,
    [PERMISSIONS.CUSTOMERS_VIEW]: true,
    [PERMISSIONS.CUSTOMERS_CREATE]: true,
    [PERMISSIONS.CUSTOMERS_EDIT]: true,
    [PERMISSIONS.CUSTOMERS_DELETE]: true,
    [PERMISSIONS.INVOICES_VIEW]: true,
    [PERMISSIONS.INVOICES_CREATE]: true,
    [PERMISSIONS.INVOICES_EDIT]: true,
    [PERMISSIONS.INVOICES_DELETE]: true,
    [PERMISSIONS.INVOICES_CANCEL]: true,
    [PERMISSIONS.SETTINGS_VIEW]: true,
    [PERMISSIONS.SETTINGS_EDIT]: true,
    [PERMISSIONS.NUMBER_SCHEMA_VIEW]: true,
    [PERMISSIONS.NUMBER_SCHEMA_EDIT]: true,
  },
  
  User: {
    // Restricted access
    [PERMISSIONS.INVOICE_DESIGN_VIEW]: false,
    [PERMISSIONS.INVOICE_DESIGN_CREATE]: false,
    [PERMISSIONS.INVOICE_DESIGN_EDIT]: false,
    [PERMISSIONS.INVOICE_DESIGN_DELETE]: false,
    [PERMISSIONS.COMPANY_VIEW]: true,
    [PERMISSIONS.COMPANY_EDIT]: false,
    [PERMISSIONS.SHIPPING_VIEW]: true,
    [PERMISSIONS.SHIPPING_CREATE]: false,
    [PERMISSIONS.SHIPPING_EDIT]: false,
    [PERMISSIONS.SHIPPING_DELETE]: false,
    [PERMISSIONS.CUSTOMERS_VIEW]: true,
    [PERMISSIONS.CUSTOMERS_CREATE]: false,
    [PERMISSIONS.CUSTOMERS_EDIT]: false,
    [PERMISSIONS.CUSTOMERS_DELETE]: false,
    [PERMISSIONS.INVOICES_VIEW]: true,
    [PERMISSIONS.INVOICES_CREATE]: true,
    [PERMISSIONS.INVOICES_EDIT]: true,
    [PERMISSIONS.INVOICES_DELETE]: true,
    [PERMISSIONS.INVOICES_CANCEL]: true,
    [PERMISSIONS.SETTINGS_VIEW]: false,
    [PERMISSIONS.SETTINGS_EDIT]: false,
    [PERMISSIONS.NUMBER_SCHEMA_VIEW]: true,
    [PERMISSIONS.NUMBER_SCHEMA_EDIT]: false,
  },
  
  Inspector: {
    // View-only access
    [PERMISSIONS.INVOICE_DESIGN_VIEW]: true,
    [PERMISSIONS.INVOICE_DESIGN_CREATE]: false,
    [PERMISSIONS.INVOICE_DESIGN_EDIT]: false,
    [PERMISSIONS.INVOICE_DESIGN_DELETE]: false,
    [PERMISSIONS.COMPANY_VIEW]: true,
    [PERMISSIONS.COMPANY_EDIT]: false,
    [PERMISSIONS.SHIPPING_VIEW]: true,
    [PERMISSIONS.SHIPPING_CREATE]: false,
    [PERMISSIONS.SHIPPING_EDIT]: false,
    [PERMISSIONS.SHIPPING_DELETE]: false,
    [PERMISSIONS.CUSTOMERS_VIEW]: true,
    [PERMISSIONS.CUSTOMERS_CREATE]: false,
    [PERMISSIONS.CUSTOMERS_EDIT]: false,
    [PERMISSIONS.CUSTOMERS_DELETE]: false,
    [PERMISSIONS.INVOICES_VIEW]: true,
    [PERMISSIONS.INVOICES_CREATE]: false,
    [PERMISSIONS.INVOICES_EDIT]: false,
    [PERMISSIONS.INVOICES_DELETE]: false,
    [PERMISSIONS.INVOICES_CANCEL]: false,
    [PERMISSIONS.SETTINGS_VIEW]: false,
    [PERMISSIONS.SETTINGS_EDIT]: false,
    [PERMISSIONS.NUMBER_SCHEMA_VIEW]: true,
    [PERMISSIONS.NUMBER_SCHEMA_EDIT]: false,
  },
};
